/**
 * Message popup invitation loaded inside its own iframe
 */
import popupMessageTemplate from '../templates/invitations/popupMessage/popupmessage.ejs';
import ChatMode from '../core/hooks/ChatMode';
import { BaseWidget } from '../core/BaseWidget';
import WidgetTypes from '../core/enums/WidgetTypes';
import WidgetView from '../core/WidgetView';
import WidgetStates from '../core/hooks/WidgetStates';
import setChatPopupMessageStyles from '../core/hooks/setChatPopupMessageStyles';
import GetSkinTemplate from '../core/hooks/GetSkinTemplate';
import TempCache from '../core/enums/TempCache';
import sanitizeHtml from '../core/hooks/sanitizeHTML';

export default class PopupMessageInvitation extends BaseWidget {
    static CONSTANTS = {
        WRAPPER_ID: 'apexchat_popup_message_invitation_max',
        WIDGET_WRAPPER_ID: 'livechat_message_popup_wrapper',
        IFRAME_SELECTOR: '.apexchat_popup_message_invitation_frame',
        CLOSE_TRIGGER_ID: 'livechat_message_popup_close',
        START_CHAT_ID: 'livechat_message_popup_start_chat',
        MIN_HEIGHT: 180,
        MIN_WIDTH: 320,
        PADDING: 30,
        DEFAULT_DELAY: 10
    };

    constructor(options) {
        super(
            Object.assign(options, {
                widget: WidgetTypes.PopupMessage,
                styleName: 'popupMessage'
            })
        );
        this.popupMessageConfig = options.popupMessage ?? {};
        this.init();
    }

    init() {
        const popupMessageMaximized = ApexChat.storageAPI.Get(WidgetTypes.PopupMessage);
        let showTimeOut = TempCache.PopupMessage_INITIAL_LOAD_TIME;

        if (!popupMessageMaximized || popupMessageMaximized === WidgetStates.ShownMaximized) {
            showTimeOut = 0;
        }

        setTimeout(() => this.handleInitialization(), showTimeOut);
    }

    async handleInitialization() {
        if (!this.shouldShowPopup()) {
            this.state.changeState({ origin: WidgetTypes.PopupMessage, toState: WidgetStates.Hidden });
            return;
        }
        const { generatePopUpTextFromAi = false } = this.popupMessageConfig;
        if (generatePopUpTextFromAi && this.options.searchResult.keywords) {
            await this.fetchAiText();
        }
        this.eventSubscriptions();
        this.ready();
    }

    eventSubscriptions() {
        this.subscribe(WidgetTypes.Windowed + WidgetStates.ShownMaximized, this.hide.bind(this));
        this.subscribe(WidgetTypes.DOM + WidgetStates.ShownMaximized, this.hide.bind(this));
        this.subscribe(WidgetTypes.ExitPopup + WidgetStates.ShownMaximized, this.hide.bind(this));
        this.subscribe(WidgetTypes.DropDown + WidgetStates.ShownMinimized, this.hide.bind(this));
        this.subscribe(WidgetTypes.DropDown + WidgetStates.Clicked, this.hide.bind(this));
        this.subscribe(WidgetTypes.Bar + WidgetStates.Clicked, this.hide.bind(this));
        this.subscribe(WidgetTypes.Tab + WidgetStates.Clicked, this.hide.bind(this));
        this.subscribe(WidgetTypes.PopupMessage + WidgetStates.Clicked, this.hide.bind(this));
        this.subscribe(WidgetTypes.PopupMessage + WidgetStates.Hidden, () => {
            this.state.changeState({ origin: WidgetTypes.PopupMessage, toState: WidgetStates.Closed });
        });
        this.subscribe(WidgetTypes.PreChat + WidgetStates.Clicked, this.hide.bind(this));
    }

    shouldShowPopup() {
        const isChatIdAlive = this.isChatInProgress();
        const domState = ApexChat.storageAPI.Get(WidgetTypes.DOM);
        const dropDownState = ApexChat.storageAPI.Get(WidgetTypes.DropDown);
        const exitPopupState = ApexChat.storageAPI.Get(WidgetTypes.ExitPopup);

        // don't show if chat is alive or dom is already shown
        return !(
            isChatIdAlive ||
            domState === WidgetStates.ShownMaximized ||
            domState === WidgetStates.ShownMinimized ||
            domState === WidgetStates.Closed ||
            dropDownState === WidgetStates.ShownMinimized ||
            exitPopupState === WidgetStates.ShownMaximized
        );
    }

    async fetchAiText() {
        const searchTerm = this.options.searchResult.keywords ?? "";
        const apiPath = `${env.AIBotServiceUrl}api/generate-text-from-search?searchTerm=${searchTerm}`;

        try {
            const response = await fetch(apiPath);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            this.popUpTextFromAi = data;
        } catch (error) {
            console.error("Error fetching/parsing popup text from API:", error);
        }
    }

    ready() {
        super.ready();
        this.create();
    }

    create() {
        super.create();
        const me = this;
        const polyfills = [];
        const handleWidgetFont = function () { };

        const config = this.getTemplateConfig();

        this.view = new WidgetView({
            dom: this.DOM,
            identifier: WidgetTypes.PopupMessage,
            templates: [
                {
                    key: WidgetStates.ShownMaximized,
                    tpl: popupMessageTemplate(config),
                    model: config,
                    polyfills,
                    afterRender: (tpl) => me.handleAfterRender(tpl)
                }
            ],
            initialState: WidgetStates.ShownMaximized,
            polyfills: [handleWidgetFont]
        });
    }

    getTemplateConfig() {
        const { popUpMessageText, popUpMessageDelaySeconds } = this.popupMessageConfig
        const sanitizedDescription = sanitizeHtml(this.popUpTextFromAi ?? popUpMessageText);
        return {
            description: sanitizedDescription,
            duration: (popUpMessageDelaySeconds || PopupMessageInvitation.CONSTANTS.DEFAULT_DELAY) * 1000,
            closeButtonText: 'No',
            submitButtonText: 'Yes'
        };
    }

    handleAfterRender(tpl) {
        this.bindEvents();
        this.applyStyles();
        this.show();
    }

    adjustIframeSize() {
        const wrapper = this.DOM.getElementById(PopupMessageInvitation.CONSTANTS.WRAPPER_ID);
        const iframe = document.querySelector(PopupMessageInvitation.CONSTANTS.IFRAME_SELECTOR);

        if (!wrapper || !iframe) return;

        const contentHeight = Math.max(wrapper.scrollHeight, PopupMessageInvitation.CONSTANTS.MIN_HEIGHT);
        const contentWidth = Math.max(wrapper.scrollWidth, PopupMessageInvitation.CONSTANTS.MIN_WIDTH);

        iframe.style.height = `${contentHeight + PopupMessageInvitation.CONSTANTS.PADDING}px`;
        iframe.style.width = `${contentWidth + PopupMessageInvitation.CONSTANTS.PADDING}px`;
    }

    bindEvents() {
        const closeTrigger = this.DOM.getElementById(PopupMessageInvitation.CONSTANTS.CLOSE_TRIGGER_ID);
        if (closeTrigger) {
            closeTrigger.addEventListener('click', () => this.hide());
        }
        if (this.options.chatMode === ChatMode.Windowed) {
            this.bindWindowedHandler(
                `#${PopupMessageInvitation.CONSTANTS.START_CHAT_ID}`,
                WidgetTypes.PopupMessage
            );
        } else {
            this.setClickCallToAction(
                PopupMessageInvitation.CONSTANTS.START_CHAT_ID,
                'PopupMessage',
            );
        }
    }

    applyStyles() {
        const styleConfig = GetSkinTemplate(this.options.rdThemeSettings);
        setChatPopupMessageStyles(this.DOM, {
            ...styleConfig,
            webToText: {},
            isRDPlusTheme: ApexChat.IsRoundedDynamicPlusTheme
        });
        const wrapper = this.DOM.getElementById(PopupMessageInvitation.CONSTANTS.WIDGET_WRAPPER_ID);
        if (wrapper) {
            setTimeout(() => {
                wrapper.classList.add('apexchat-popup-message-visible');
            }, 200)
        }
    }

    show() {
        super.show();
        this.maximize();
    }

    maximize() {
        super.maximize();
    }
}
